import React, { useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { colorArray } from "data/barchart";
import {
  Box,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  ChartDataLabels
);

const mediaBiasLabels = ["L", "", "C", "", "R"];

const categoryKeys = [
  "Periodicals & Specialty News",
  "Think Tanks",
  "Global News",
  "Academia & Scholarly Resources",
  "US Government",
  "Intergovernmental Organizations",
  "Other Selected Sources",
];

const wrapLabel = (text, maxChars = 10) => {
  const words = text.split(" ");
  const lines = [];
  let currentLine = "";

  words.forEach((word) => {
    if (word.length > maxChars) {
      if (currentLine.trim()) {
        lines.push(currentLine.trim());
        currentLine = "";
      }
      for (let i = 0; i < word.length; i += maxChars) {
        let chunk = word.slice(i, i + maxChars);
        if (i + maxChars < word.length) {
          lines.push(chunk + "-");
        } else {
          currentLine = chunk + " ";
        }
      }
      return;
    }

    if ((currentLine + word).length > maxChars && currentLine.length > 0) {
      lines.push(currentLine.trim());
      currentLine = "";
    }
    currentLine += word + " ";
  });
  if (currentLine.trim()) {
    lines.push(currentLine.trim());
  }
  return lines;
};

const categoryLabels = categoryKeys.map((key) => wrapLabel(key, 10));

const categoryColors = [
  "#1565c0",
  "#2e7d32",
  "#e65100",
  "#6a1b9a",
  "#b71c1c",
  "#00695c",
  "#4e342e",
];

const CHART_OPTIONS = [
  { value: "mediaBias", label: "Media Bias" },
  { value: "category", label: "Category Spread" },
];


export default function MediaBiasChart({ allSidesData, categoryData, selectedChart, setSelectedChart }) {
  const theme = useTheme();


  const isMediaBias = selectedChart === "mediaBias";

  // --- Media Bias data ---
  const mediaBiasArray = allSidesData ? Object.values(allSidesData) : [];
  const formattedMediaBias = mediaBiasArray.map((v) => (v ? parseInt(v) : 0));

  // --- Category data ---
  const categoryValues = categoryKeys.map((key) => (categoryData ? Math.round(categoryData[key] || 0) : 0));
  const notCategorizedValue = categoryData ? Math.round(categoryData["NotAvailable"] || 0) : 0;

  const getChartOptions = (type) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        callbacks: {
          title: (tooltipItems) => {
            const title = tooltipItems[0].label;
            return Array.isArray(title) ? title.join(" ") : title;
          },
          label: (context) => {
            return `${context.dataset.label}: ${context.parsed.y}%`;
          }
        }
      },
      datalabels: {
        anchor: "end",
        align: "end",
        color: theme.palette.grey[700],
        font: { size: type === "category" ? 11 : 14 },
        formatter: (value) => (value ? `${value}%` : ""),
      },
    },
    scales: {
      x: {
        position: "top",
        border: { display: false },
        grid: { lineWidth: 0 },
        ticks: {
          display: true,
          autoSkip: false,
          padding: type === "mediaBias" ? 25 : 10,
          showLabelBackdrop: type === "mediaBias",
          backdropColor: "#a3becf",
          color: type === "mediaBias" ? theme.palette.common.white : theme.palette.text.secondary,
          font: {
            size: type === "category" ? 10 : 12,
            weight: type === "category" ? 600 : "normal",
          },
        },
      },
      y: {
        display: false,
        grid: { display: false },
      },
    },
  });

  const mediaBiasChartData = {
    labels: mediaBiasLabels,
    datasets: [
      {
        label: "Bias Distribution",
        data: formattedMediaBias.slice(0, -1),
        backgroundColor: colorArray,
        borderColor: colorArray,
        borderWidth: 1,
        categoryPercentage: 1.0,
        barPercentage: 0.9,
      },
    ],
  };

  const categoryChartData = {
    labels: categoryLabels,
    datasets: [
      {
        label: "Category Distribution",
        data: categoryValues,
        backgroundColor: categoryColors,
        borderColor: categoryColors,
        borderWidth: 1,
        categoryPercentage: 0.8,
        barPercentage: 0.6,
      },
    ],
  };

  const noRatingValue = isMediaBias ? formattedMediaBias[5] : notCategorizedValue;
  const noRatingLabel = isMediaBias ? "No rating" : "Not Categorized";
  const chartTitle = isMediaBias ? (
    <>
      AllSides Media Bias Rating
      <Typography component="sup" variant="h6" color="inherit" fontSize="0.6em">
        TM
      </Typography>
    </>
  ) : (
    "Category Spread"
  );

  if (isMediaBias) {
    return (
      <Stack
        direction="column"
        sx={{ gridArea: "ratings" }}
        gap={1}
        minHeight={120}
        width="100%"
      >
        <FormControl size="small" sx={{ alignSelf: "flex-start", minWidth: 170 }}>
          <Select
            value={selectedChart}
            onChange={(e) => setSelectedChart(e.target.value)}
            displayEmpty
            variant="outlined"
            sx={{ fontSize: "0.8rem" }}
          >
            {CHART_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Stack direction={{ xs: "column", md: "row" }} flex={1} minHeight={0} height="100%" gap={{ xs: 2, md: 0 }}>
          <Stack
            direction="column"
            height={{ xs: "auto", md: "100%" }}
            justifyContent={{ xs: "flex-start", md: "space-between" }}
            width={{ xs: "100%", md: "30%" }}
            alignItems="flex-start"
            gap={{ xs: 2, md: "5%" }}
          >
            <Typography color="secondary.contrastText" variant="body2">
              {chartTitle}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "end" }}>
              <Typography color="secondary.contrastText" variant="body2">
                {noRatingLabel}
              </Typography>
              <Typography variant="body2">{noRatingValue}%</Typography>
            </Box>
          </Stack>
          <Box
            width={{ xs: "100%", md: "65%" }}
            height={{ xs: "120px", md: "100%" }}
            position="relative"
            sx={{ borderBottom: `1px solid ${theme.palette.grey[400]}` }}
          >
            <Bar
              data={mediaBiasChartData}
              options={getChartOptions("mediaBias")}
              width="100%"
              height="100%"
            />
          </Box>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack
      direction={{ xs: "column", lg: "row" }}
      sx={{ gridArea: "ratings", maxWidth: "100%", overflow: "hidden" }}
      gap={4}
      minHeight={{ xs: 90, lg: 160 }}
      width="100%"
    >
      <Stack
        direction="column"
        height={{ xs: "auto", lg: "100%" }}
        justifyContent={{ xs: "flex-start", lg: "space-between" }}
        width={{ xs: "100%", lg: "25%" }}
        alignItems="flex-start"
        gap={{ xs: 2, lg: "5%" }}
      >
        <Stack gap={1} width="100%">
          <FormControl size="small" sx={{ alignSelf: "flex-start", minWidth: 150 }}>
            <Select
              value={selectedChart}
              onChange={(e) => setSelectedChart(e.target.value)}
              displayEmpty
              variant="outlined"
              sx={{ fontSize: "0.8rem" }}
            >
              {CHART_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography color="secondary.contrastText" variant="body2" sx={{ mt: 1 }}>
            {chartTitle}
          </Typography>
        </Stack>

        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "end" }}>
          <Typography color="secondary.contrastText" variant="body2">
            {noRatingLabel}
          </Typography>
          <Typography variant="body2">{noRatingValue}%</Typography>
        </Box>
      </Stack>

      <Box
        width={{ xs: "100%", lg: "75%" }}
        height={{ xs: "280px", lg: "100%" }}
        position="relative"
        sx={{ 
          maxWidth: "100%",
          borderBottom: `1px solid ${theme.palette.grey[400]}`, 
          overflowX: "auto", 
          overflowY: "hidden", 
          pb: { xs: 2, lg: 0 },
          "&::-webkit-scrollbar": {
            height: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: theme.palette.grey[600],
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: theme.palette.grey[500],
          }
        }}
      >
        <Box sx={{ minWidth: { xs: "600px", md: "100%" }, height: "100%" }}>
          <Bar
            data={categoryChartData}
            options={getChartOptions("category")}
            width="100%"
            height="100%"
          />
        </Box>
      </Box>
    </Stack>
  );
}
